import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { TableSpinner } from "../../components";
import { State, UserProfileDto } from "../../interfaces";
import DeleteModal from "../../components/Common/DeleteModal";
import smallImage13 from "../../assets/images/small/img-13.png";
import { AddEditFlashCardModal } from "./AddEditFlashCardModal";
import {
  deleteAllFlashCard,
  deleteFlashCard,
  deleteFlashCardConsent,
  getFlashCardsByLesson,
  toggleAddEditFlashCardModal,
  toggleFlashCardState,
} from "../../store/actions";
import { RoleEnum } from "../../enums";
import { FlashCardDto } from "../../interfaces/flash-card";
import FlashCardImageUploader from "../../components/Common/FlashCardImageUploader";

type TProps = {
  lessonId: string;
  header?: any;
};
export const FlashCardPage: React.FC<TProps> = ({ lessonId, header }) => {
  //#region hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const [showFlashCardDeleteModal, setShowFlashCardDeleteModal] =
    useState<boolean>(false);
  const [showFlashCardDeleteAllModal, setShowFlashCardDeleteAllModal] =
    useState<boolean>(false);
  const [deletingFlashCardId, setDeletingFlashCardId] = useState<string | null>(
    null
  );
  const [showAddEditFlashCardModal, setShowAddEditFlashCardModal] =
    useState<boolean>(false);
  const [editingFlashCardId, setEditingFlashCardId] = useState<string | null>(
    null
  );
  //#endregion

  //#region redux state
  const { loading: loadingFlashCards, response: flashCards } = useSelector(
    (state: any): State<FlashCardDto[]> => state.flashCard.flashCardByLesson
  );
  const {
    loading: deleteFlashCardLoading,
    success: deleteFlashCardSuccess,
    error: deleteFlashCardError,
  } = useSelector(
    (state: any): State<FlashCardDto> => state.flashCard.deleteFlashCard
  );

  const {
    loading: deleteAllFlashCardLoading,
    success: deleteAllFlashCardSuccess,
    error: deleteAllFlashCardError,
  } = useSelector(
    (state: any): State<any> => state.flashCard.deleteAllFlashCardByLessonId
  );

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );

  //#endregion

  //#region effects
  useEffect(() => {
    dispatch(getFlashCardsByLesson(lessonId, history));
  }, [lessonId]);

  useEffect(() => {
    if (!deleteFlashCardLoading && deleteFlashCardSuccess) {
      setDeletingFlashCardId(null);
      setShowFlashCardDeleteModal(false);
    }
  }, [deleteFlashCardLoading]);

  useEffect(() => {
    if (!deleteAllFlashCardLoading && deleteAllFlashCardSuccess) {
      setShowFlashCardDeleteAllModal(false);
    }
  }, [deleteAllFlashCardLoading]);
  //#endregion

  //#region handlers

  const onAdd = () => {
    dispatch(toggleAddEditFlashCardModal());
    setShowAddEditFlashCardModal(true);
  };

  const onEdit = (id: string) => {
    setEditingFlashCardId(id);
    setShowAddEditFlashCardModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingFlashCardId(id);
    setShowFlashCardDeleteModal(true);
    dispatch(deleteFlashCardConsent(id));
  };

  const onDeleteAll = () => {
    setShowFlashCardDeleteAllModal(true);
  };

  const onDeleteConfirm = () => {
    dispatch(deleteFlashCard(deletingFlashCardId as string, history));
    setDeletingFlashCardId(null);
  };

  const onDeleteAllConfirm = () => {
    dispatch(deleteAllFlashCard(lessonId, history));
  };

  const onToggleState = (id: string) => {
    dispatch(toggleFlashCardState(id as string, history));
  };

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">{header}</h4>
            </div>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              <Button color="success" onClick={() => onAdd()}>
                <i className="ri-add-fill me-1 align-bottom"></i>
                {t("addItem", { item: "flashCard" })}
              </Button>
              <Button color="success" onClick={() => onDeleteAll()}>
                <i className="ri-delete-bin-line me-1 align-bottom"></i>
                {t("deleteAll")}
              </Button>
              <Button
                color="success"
                onClick={() =>
                  dispatch(getFlashCardsByLesson(lessonId, history))
                }
              >
                {t("refresh")}
              </Button>
            </div>
          </Col>
        </Row>
        <Row>
          <FlashCardImageUploader lessonId={lessonId} />
        </Row>
        <Row className="mt-3">
          {loadingFlashCards && <TableSpinner />}
          {!loadingFlashCards && flashCards && flashCards.length > 0 && (
            <React.Fragment>
              <Col lg={12}>
                <Row className="team-list grid-view-filter">
                  {flashCards.map((item: FlashCardDto, key) => (
                    <Col key={key} xs={12} sm={6} lg={4} xl={3} xxl={3}>
                      <Card className="team-box">
                        <img
                          src={item.image?.path || smallImage13}
                          alt=""
                          style={{ objectFit: "cover", height: 250 }}
                        />
                        <CardBody>
                          <UncontrolledDropdown
                            style={
                              i18n.language === "ar"
                                ? { position: "absolute", top: 10, left: 15 }
                                : { position: "absolute", top: 10, right: 15 }
                            }
                          >
                            <DropdownToggle
                              tag="a"
                              id="dropdownMenuLink2"
                              role="button"
                            >
                              <i className="ri-more-fill fs-17"></i>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem
                                style={{
                                  alignSelf: "flex-start",
                                  display: "flex",
                                }}
                                className="dropdown-item edit-list"
                                onClick={() => onEdit(item.id)}
                              >
                                <i className="ri-pencil-line me-2 align-bottom text-muted"></i>
                                {t("edit")}
                              </DropdownItem>
                              {profile &&
                                profile.role.key == RoleEnum.SUPER_ADMIN && (
                                  <DropdownItem
                                    style={{
                                      alignSelf: "flex-start",
                                      display: "flex",
                                    }}
                                    className="dropdown-item edit-list"
                                    onClick={() => onToggleState(item.id)}
                                  >
                                    <i
                                      className={`${
                                        item.isActive
                                          ? "ri-forbid-line"
                                          : "ri-checkbox-circle-line text-danger"
                                      }  me-2 align-bottom text-muted`}
                                    ></i>
                                    {item.isActive
                                      ? t("inactivate")
                                      : t("activate")}
                                  </DropdownItem>
                                )}
                              <DropdownItem
                                style={{
                                  alignSelf: "flex-start",
                                  display: "flex",
                                }}
                                className="dropdown-item remove-list"
                                onClick={() => onDelete(item.id)}
                              >
                                <i className="ri-delete-bin-5-line me-2 align-bottom text-muted"></i>
                                {t("delete")}
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>

                          <Row>
                            <Col sm={12} className="mb-2">
                              <Badge
                                color={`${
                                  item.isActive ? "primary" : "danger"
                                }`}
                              >
                                {item.isActive ? t("active") : t("inActive")}
                              </Badge>
                              &nbsp;
                              <Badge color={"primary"}>
                                {item.sequenceNumber}
                              </Badge>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
            </React.Fragment>
          )}
        </Row>
        <DeleteModal
          loading={deleteFlashCardLoading}
          show={showFlashCardDeleteModal}
          onDeleteClick={() => onDeleteConfirm()}
          onCloseClick={() => {
            setShowFlashCardDeleteModal(false);
          }}
          error={deleteFlashCardError}
        />
        <DeleteModal
          loading={deleteAllFlashCardLoading}
          show={showFlashCardDeleteAllModal}
          onDeleteClick={() => onDeleteAllConfirm()}
          message={"deleteConfirmationAll"}
          onCloseClick={() => {
            setShowFlashCardDeleteAllModal(false);
          }}
          error={deleteAllFlashCardError}
        />
        <AddEditFlashCardModal
          lessonId={lessonId}
          id={editingFlashCardId}
          show={showAddEditFlashCardModal}
          onCloseClick={() => {
            dispatch(toggleAddEditFlashCardModal());
            setShowAddEditFlashCardModal(false);
            setEditingFlashCardId(null);
            dispatch(getFlashCardsByLesson(lessonId, history));
          }}
        />
      </Container>
    </React.Fragment>
  );
};
